import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
} from "reactstrap";
import swal from "sweetalert";
import { useNavigate, useParams } from "react-router-dom";

import { ProductListView, _Get } from "../import/ApiEndPoint/ApiCalling";
import { All_Users_List } from "../import/ApiEndPoint/Api";
import { baseURL } from "../import/ApiEndPoint/TestApi";

/* ------------------ helpers ------------------ */
const getUserDisplayName = (u) => {
  const fn = u?.firstName || u?.name || "";
  const ln = u?.lastName || "";
  return `${fn} ${ln}`.trim() || "—";
};

// FY helpers
const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];
const currentFYLabel = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth(); // 0=Jan
  const start = m >= 3 ? y : y - 1;
  const endShort = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${endShort}`;
};
const monthYearForFY = (monthName, fyLabel) => {
  const startYear = parseInt(String(fyLabel).split("-")[0], 10);
  if (!Number.isFinite(startYear)) return "";
  const endYear = startYear + 1;
  const lastQuarter = ["January", "February", "March"];
  const year = lastQuarter.includes(monthName) ? endYear : startYear;
  return `${monthName}-${year}`;
};

const EditSalesPersonTarget = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [Loading, setLoading] = useState(true);
  const [Saving, setSaving] = useState(false);

  const [ProductList, setProductList] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState("");

  const [financialYear, setFinancialYear] = useState(currentFYLabel());
  const [monthName, setMonthName] = useState("");
  const [incrementPercent, setIncrementPercent] = useState(10);

  const [targetQtyMap, setTargetQtyMap] = useState({});
  const [achievedMap, setAchievedMap] = useState({});

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData") || "{}"),
    []
  );

  // endpoints
  const SalesPersonTarget_ById = "/salesperson-target";
  const SalesPersonTarget_Update = "/salesperson-target/update";
  const SalesPersonTarget_Delete = "/salesperson-target/delete";

  /* ---------- Load initial data ---------- */
  useEffect(() => {
    const load = async () => {
      try {
        if (!userData?._id || !userData?.database || !id) {
          setLoading(false);
          return;
        }

        // 1) products
        const pRes = await ProductListView(userData._id, userData.database);
        const prodList = pRes?.Product || [];
        setProductList(prodList);

        // default achieved = 0
        const draftAch = {};
        prodList.forEach((p) => {
          if (p?._id) draftAch[p._id] = 0;
        });
        setAchievedMap(draftAch);

        // 2) sales persons
        const uRes = await _Get(All_Users_List, userData.database);
        const list =
          uRes?.User || uRes?.users || uRes?.adminDetails || uRes?.data || [];
        const activeSales = (Array.isArray(list) ? list : []).filter(
          (u) =>
            u?.rolename?.roleName === "Sales Person" && u?.status !== "Deactive"
        );
        setSalesPersons(activeSales);

        // 3) existing target
        const res = await fetch(
          `${baseURL}${SalesPersonTarget_ById}/${id}?database=${encodeURIComponent(
            userData.database || ""
          )}`
        );
        const data = await res.json();
        if (!data?.status || !data?.data) {
          swal("Error", data?.message || "Target not found", "error");
          setLoading(false);
          return;
        }

        const doc = data.data;

        setFinancialYear(doc.financialYear || currentFYLabel());
        setMonthName(doc.monthName || "");
        setIncrementPercent(
          doc.incrementPercent != null ? doc.incrementPercent : 10
        );

        const spId = doc.salesPersonId;
        setSelectedSalesPersonId(spId ? String(spId) : "");

        const qtyMap = {};
        (doc.products || []).forEach((p) => {
          const pid =
            p?.productId?._id || p?.productId?.id || p?.productId || null;
          if (pid) qtyMap[pid] = Number(p.qtyAssign || 0);
        });
        setTargetQtyMap(qtyMap);
      } catch (err) {
        console.error("[EditSalesPersonTarget] load error:", err);
        swal("Error", "Failed to load target", "error");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, userData]);

  /* ---------- Derived rows & totals ---------- */
  const rows = useMemo(() => {
    return ProductList.map((p) => {
      const price = Number(p?.SalesRate || 0);
      const targetQty = Number(targetQtyMap[p._id] || 0);
      const achieved = Number(achievedMap[p._id] || 0);
      const shortfall = Math.max(targetQty - achieved, 0);
      const total = targetQty * price;
      return {
        _id: p._id,
        sId: p.sId,
        name: p?.Product_Title || "—",
        price,
        achieved,
        targetQty,
        shortfall,
        total,
      };
    });
  }, [ProductList, targetQtyMap, achievedMap]);

  const grandTotal = useMemo(
    () =>
      rows.reduce(
        (sum, r) => sum + (Number.isFinite(r.total) ? r.total : 0),
        0
      ),
    [rows]
  );

  const setTargetQty = (productId, value) => {
    const v = Math.max(Number(value || 0), 0);
    setTargetQtyMap((m) => ({ ...m, [productId]: v }));
  };

  /* ---------- Save / update ---------- */
  const submitHandler = async (e) => {
    e.preventDefault();
    if (!selectedSalesPersonId)
      return swal("Error", "Choose Sales Person", "error");
    if (!monthName) return swal("Error", "Choose Target Month", "error");
    if (!financialYear) return swal("Error", "Choose Financial Year", "error");

    const sp = salesPersons.find(
      (u) => String(u._id) === String(selectedSalesPersonId)
    );

    const products = rows
      .filter((r) => Number(r.targetQty) > 0)
      .map((r) => ({
        productId: r._id,
        qtyAssign: Number(r.targetQty),
        price: Number(r.price),
        totalPrice: Number((r.price * r.targetQty).toFixed(2)),
        assignPercentage: [],
      }));

    if (!products.length) {
      return swal(
        "Error",
        "Please enter target quantity for at least one product",
        "error"
      );
    }

    const payload = {
      monthName,
      financialYear,
      date: monthYearForFY(monthName, financialYear),
      salesPersonId: selectedSalesPersonId,
      salesPersonName: sp ? getUserDisplayName(sp) : "—",
      products,
      incrementPercent: Number(incrementPercent) || 0,
      grandTotal: Number(grandTotal.toFixed(2)),
      database: userData?.database,
    };

    try {
      setSaving(true);
      const res = await fetch(
        `${baseURL}${SalesPersonTarget_Update}/${id}?database=${encodeURIComponent(
          userData.database || ""
        )}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      setSaving(false);

      if (data?.status) {
        swal("Updated", "Sales Person Target updated successfully", "success");
        navigate(-1);
      } else {
        swal("Error", data?.message || "Failed to update target", "error");
      }
    } catch (err) {
      setSaving(false);
      console.error("[EditSalesPersonTarget] update error:", err);
      swal("Error", "Failed to update target", "error");
    }
  };

  /* ---------- Delete ---------- */
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this target?")) return;

    try {
      setSaving(true);
      const res = await fetch(
        `${baseURL}${SalesPersonTarget_Delete}/${id}?database=${encodeURIComponent(
          userData.database || ""
        )}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      setSaving(false);

      if (data?.status) {
        swal("Deleted", "Sales Person Target deleted successfully", "success");
        navigate(-1);
      } else {
        swal("Error", data?.message || "Failed to delete target", "error");
      }
    } catch (err) {
      setSaving(false);
      console.error("[EditSalesPersonTarget] delete error:", err);
      swal("Error", "Failed to delete target", "error");
    }
  };

  // Build FY list (current FY ± 2)
  const fyOptions = useMemo(() => {
    const cur = parseInt(currentFYLabel().split("-")[0], 10);
    const arr = [];
    for (let y = cur - 2; y <= cur + 2; y++) {
      const endShort = String((y + 1) % 100).padStart(2, "0");
      arr.push(`${y}-${endShort}`);
    }
    return arr.reverse();
  }, []);

  if (Loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <Row className="m-2">
          <Col>
            <h1>Edit Sales Person Target</h1>
          </Col>
          <Col className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button color="danger" onClick={handleDelete} disabled={Saving}>
              {Saving ? "Deleting..." : "Delete Target"}
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form className="m-1" onSubmit={submitHandler}>
            <Row>
              {/* Sales Person dropdown */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Sales Person</Label>
                <Input
                  required
                  type="select"
                  value={selectedSalesPersonId}
                  onChange={(e) => setSelectedSalesPersonId(e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {salesPersons.map((sp) => (
                    <option key={sp?._id} value={sp?._id}>
                      {getUserDisplayName(sp)}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Financial Year */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Financial Year</Label>
                <Input
                  type="select"
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                >
                  <option value="">Select FY</option>
                  {fyOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Month (FY order) */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Target Month (FY)</Label>
                <Input
                  type="select"
                  value={monthName}
                  onChange={(e) => setMonthName(e.target.value)}
                >
                  <option value="">Select Month</option>
                  {FY_MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Increment % */}
              <Col className="mb-1" lg="3" md="4" sm="12">
                <Label>Enter Increment %</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g. 10"
                  value={incrementPercent}
                  onChange={(e) => setIncrementPercent(e.target.value)}
                />
              </Col>
            </Row>

            {/* ROW-WISE PRODUCTS */}
            <div className="table-responsive mt-2">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Product</th>
                    <th>Achieved Qty</th>
                    <th>Target Qty</th>
                    <th>Shortfall Qty</th>
                    <th>Price (SalesRate)</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r._id}>
                      <td>{r.name}</td>
                      <td>
                        <Input type="number" readOnly value={r.achieved} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={r.targetQty}
                          onChange={(e) => setTargetQty(r._id, e.target.value)}
                        />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.shortfall} />
                      </td>
                      <td>
                        <Input type="number" readOnly value={r.price} />
                      </td>
                      <td>
                        <Input
                          type="number"
                          readOnly
                          value={r.total.toFixed(2)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={5} className="text-right">
                      Grand Total
                    </th>
                    <th>
                      <Input
                        type="number"
                        readOnly
                        value={grandTotal.toFixed(2)}
                      />
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Row className="mt-2">
              <Col className="d-flex justify-content-center">
                <Button color="primary" type="submit" disabled={Saving}>
                  {Saving ? "Saving..." : "Update"}
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default EditSalesPersonTarget;
